import React, { useState } from 'react';

const serviceOptions = [
  'Website',
  'Online store',
  'Mobile app',
  'Web + mobile bundle',
  'AI integration',
  'Care plan / maintenance',
  'Something else',
];

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    selectedServices: [] as string[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((d) => ({ ...d, [name]: value }));
    if (status?.type === 'error') setStatus(null);
  };

  const toggleService = (service: string) => {
    setFormData((d) => ({
      ...d,
      selectedServices: d.selectedServices.includes(service)
        ? d.selectedServices.filter((s) => s !== service)
        : [...d.selectedServices, service],
    }));
    if (status?.type === 'error') setStatus(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus(null);

    if (!formData.name || !formData.email || !formData.message || formData.selectedServices.length === 0) {
      setStatus({ type: 'error', message: 'Please fill in every field and pick at least one service.' });
      return;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const response = await fetch(`${apiBaseUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (response.ok) {
        setStatus({ type: 'success', message: data.message || "Thank you! We'll be in touch soon." });
        setFormData({ name: '', email: '', message: '', selectedServices: [] });
      } else {
        setStatus({ type: 'error', message: data.message || 'Sorry, there was an issue sending your message.' });
      }
    } catch {
      setStatus({ type: 'error', message: 'A network error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="wrap">
      <section id="contact" className="contact">
        <div className="sec-head">
          <h2>Start a project</h2>
          <span className="sec-tag">Free scope call</span>
        </div>
        <div className="contact-grid">
          <div className="contact-side">
            <p>
              Tell us what you're trying to build — or what's broken — and
              we'll come back with questions, a straight answer, and a fixed
              quote if it's a fit.
            </p>
            <ul className="mono-list">
              <li>Replies within one business day</li>
              <li>Exact quote in writing before work starts</li>
              <li>Prices published on the rate card</li>
            </ul>
          </div>
          <form onSubmit={handleSubmit} noValidate>
            {status && <div className={`form-alert ${status.type}`} role="alert">{status.message}</div>}
            <div className="field">
              <label htmlFor="name">Full name</label>
              <input id="name" name="name" type="text" value={formData.name} onChange={handleChange} required />
            </div>
            <div className="field">
              <label htmlFor="email">Email address</label>
              <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required />
            </div>
            <div className="field">
              <label>What do you need?</label>
              <div className="check-grid">
                {serviceOptions.map((s) => (
                  <label key={s} className="check">
                    <input
                      type="checkbox"
                      checked={formData.selectedServices.includes(s)}
                      onChange={() => toggleService(s)}
                    />
                    {s}
                  </label>
                ))}
              </div>
            </div>
            <div className="field">
              <label htmlFor="message">Tell us about it</label>
              <textarea id="message" name="message" value={formData.message} onChange={handleChange} required />
            </div>
            <button type="submit" className="btn green" disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : 'Send message'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};
